import React from 'react';
import { Link } from 'react-router-dom';

const GAME_URL = 'https://vertigo-climb.netlify.app/';

const post = {
  slug: 'vertigo',
  title: 'Vertigo: three climbs, sunset to dawn',
  date: '2026-09-22',
  cover: '/blog/vertigo/title.jpg',
  coverAlt:
    'The Vertigo title over a grassy terrace with a red torii gate and a lit lantern, sandstone pillars rising out of a sea of cloud behind',
  coverCaption: 'Lantern Terrace. The pagoda at the very top is where you are going.',
  excerpt:
    'Vertigo is our second browser game: three first-person climbs, from 226 metres of sandstone pillars above the clouds to a drowned city in a storm and a monks\u2019 crag at dawn. Here is what it is and how it is made.',
  content: (
    <>
      <p>
        <a href={GAME_URL}>Vertigo</a> is our second first-person game in the
        browser, and it is about one thing: going up. You start on a
        lantern-lit terrace. Ahead of you, sandstone pillars rise out of a sea
        of cloud, and on the tallest of them, far above everything else, a
        small pagoda catches the light. Somewhere up there is a bell nobody
        has rung in a long time. The first climb is the way to it, and there
        are two more after that.
      </p>

      <figure className="post-figure">
        <div className="post-video">
          <iframe
            src="https://www.youtube-nocookie.com/embed/yIaEF__2CMs"
            title="Vertigo: Sunset, Storm, Dawn"
            loading="lazy"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
          />
        </div>
        <figcaption>
          Sunset, storm, dawn: the end of each climb, filmed by the game&apos;s
          own camera after a bot has climbed there for real.
        </figcaption>
      </figure>

      <h2>The Pillars of the Sky</h2>
      <p>
        The first level is called the Pillars of the Sky, and it starts by
        sending you the wrong way. Cliff stairs take you down from the
        terrace to the cloud line, so the climb begins at the bottom of the
        world. From there it is 226 metres up: a plank bridge skimming the
        cloud tops, stepping stones you have to jump, a ladder up a
        watchtower, a glass skywalk bolted to a sheer face, a 76-metre
        suspension bridge hung with prayer flags, a glass lift up the side of
        the Great Spire, a run of jumps between needles of rock, and a stair
        cut round the last pinnacle to the bell.
      </p>

      <figure className="post-figure">
        <img
          src="/blog/vertigo/glass-walk.jpg"
          alt="Looking straight down through the Glass Walk's glass floor and railings to the cloud sea far below, pillars dropping away into it"
        />
        <figcaption>The Glass Walk. Do not look down. Everyone looks down.</figcaption>
      </figure>

      <p>
        Ring it, and if you want, there is a long way home. A cable car swings
        you 200 metres out across the chasm to the Heron&apos;s Perch, and
        from there the Cascade, nine pillars each a little lower than the
        last, lets you run and leap all the way back down to where you
        started.
      </p>

      <h2>Two more climbs</h2>
      <p>
        <strong>The Drowned Towers</strong> turns the Pillars upside down. A
        storm has settled over a half-drowned gothic city, and you start on a
        bell tower&apos;s gallery a few metres above the waves. From there it
        is 140 metres up: a broken arcade, a transporter ferry into a
        roofless cathedral, a flying buttress, a spire and a viaduct with its
        spans fallen, to a lighthouse whose lamp has been left to gutter.
        Light it and the storm lets go, and a cable carries you home over the
        calm.
      </p>
      <p>
        <strong>Kell Head</strong> is a monks&apos; crag off a northern
        coast, climbed by night into dawn. You land at a quay under the
        stars, with the fire on the summit a red spark 200 metres up, and
        climb a stair cut in the rock with a rope for a rail, sea stacks off a
        gannet cliff, the monks&apos; terraces and their beehive huts, a
        chimney of rope and iron, and a knife-edge ridge where the wind leans
        you onto the rail. At the top is an iron fire basin nobody has lit in
        living memory. Light it and the sun comes clear of the sea, and far to
        the south-west the other two goals answer: first the lighthouse, then
        the bell.
      </p>
      <p>
        Each climb also hides seven things off the route for anyone who looks:
        prayer tablets on the Pillars, the last keeper&apos;s log in the
        Drowned Towers, carved cross-slabs on Kell Head. Between them they tell
        one story.
      </p>

      <h2>Built to make you feel the height</h2>
      <p>
        Height is easy to show and hard to feel, so most of the work went
        into feel. The wind buffets you harder the higher you get. Look straight
        down a long drop and the view pulls away from you, glass floor
        included. Your footsteps change with what is under them: grass, rock,
        worn stone, planks, a bridge that creaks, glass, steel.
      </p>
      <p>
        The day moves with you. The sun sinks as your best height rises, so
        the terrace is a summer afternoon and the summit is gold and rose.
        Ring the bell and the sun goes down properly: dusk, then stars and a
        moon, and the lanterns light your way home. The music works the same
        way. It is written live as you play, a slow chord cycle that gathers
        plucks, bells and a high shimmer as you climb, marks each new personal
        best on the way up, and comes home to rest when the bell rings. M
        turns it off if you would rather have the wind.
      </p>

      <div className="post-photo-grid">
        <img
          src="/blog/vertigo/sky-bridge.jpg"
          alt="Walking the Sky Bridge, a plank suspension bridge strung with bright prayer flags, towards the Great Spire"
        />
        <img
          src="/blog/vertigo/summit.jpg"
          alt="The summit bell under a red frame at sunset, the pillar forest glowing in the haze below"
        />
      </div>

      <p>
        Movement is simple, with one forgiving extra. You walk, jump, climb
        ladders and ride lifts, and if you jump for a ledge and fall a hand
        short, you catch it and haul yourself up. Missing a jump by a whisker
        should feel like a near miss, not a punishment. When you do fall, the
        game puts you back on the last solid ground you stood on.
      </p>

      <h2>Race your best</h2>
      <p>
        Once you have been up once, the game quietly turns into a race. A
        clock runs from the moment you set off, splits tell you whether you
        are ahead of your best as you pass each band of height, and your best
        run climbs beside you as a pale, glowing ghost. Every goal ends on
        your time and a medal, where gold is about what the robot manages on
        the ideal line, and R starts you over instantly. Collect all seven
        finds on the way and it counts as a 100% run, with its own bests and
        ghost. Once you have watched your own ghost vanish up a ladder ahead
        of you, it is hard not to chase it.
      </p>

      <h2>How it is made</h2>
      <p>
        Vertigo is a sibling of <Link to="/blog/farshore">Farshore</Link>.
        It borrows Farshore&apos;s character controller, the part that
        decides how you move and collide, because every awkward edge case in
        it had already been found and fixed. We copied it rather than sharing
        it, because the two games want different things from it: Vertigo
        needed climbing, mantling and riding, and Farshore never will.
      </p>
      <p>
        The levels are built from a kit. Every piece, whether a pillar, a
        stair, a bridge or a lift, is described once, and that one
        description produces both what you see and what you can stand on.
        The two can never disagree, so there are no invisible walls and no
        floors that are not really there. Everything beyond the route, the
        far pillars, the mountains, the birds, has no collision at all, which
        is how the world can stretch to the horizon and still run in a
        browser.
      </p>
      <p>
        The kit also makes the game testable. A small robot plays Vertigo
        with the same key presses you would, and before a change goes out it
        climbs every level to its goal and back down the long way. If a stair
        is a fraction too steep or a jump a fraction too long, the robot
        falls, and we know before anyone else does.
      </p>

      <h2>Go and climb it</h2>
      <p>
        <a href={GAME_URL}>Play Vertigo here</a>. It is free and runs in a
        desktop browser with a mouse and keyboard: WASD to move, the mouse to
        look, Space to jump or let go of a ladder, E to interact, R to start
        over, Esc to pause. Pick a climb on the title card. Headphones help.
        Take the stairs down first. Trust the glass. And when you get to the
        top, ring the bell.
      </p>
    </>
  ),
};

export default post;
